import { setNotification } from "features/Notification/notificationSlice";
import { portalLoginUrl } from "pages/Portal/portalApi";
import { useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useDispatch, useSelector } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";

// "Create Portal" on a customer contact (user, 2026-11-05): staff set a
// password and the contact logs in to the customer portal with their email.
// The password is shown here only while staff type it; the API keeps a hash.
const MIN_PASSWORD = 8;

const makePassword = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  const bytes = new Uint32Array(12);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => chars[b % chars.length]).join("");
};

function ContactPortalButton({ contact, onChanged }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user?.userInfo);
  const [show, setShow] = useState(false);
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null); // { email, password } after save

  const canEdit = user?.roles === "admin" || user?.permissions?.includes("customer_update");
  if (!canEdit || !contact?.id) return null;

  const enabled = !!contact.portalEnabled;
  const loginUrl = portalLoginUrl(user?.companyId);

  const open = () => {
    setPassword(enabled ? "" : makePassword());
    setError("");
    setDone(null);
    setShow(true);
  };

  const notify = (message, type = "success") => dispatch(setNotification({ message, type }));
  const apiError = (err) => err?.response?.data?.message || err.message;

  const save = async (e) => {
    e.preventDefault();
    if (password.length < MIN_PASSWORD) {
      setError(`At least ${MIN_PASSWORD} characters`);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const { data } = await API.post(
        `/customer-contact/${contact.id}/portal`,
        { password },
        { headers: authHeader() },
      );
      setDone({ email: contact.email, password });
      notify(data?.message || "Saved");
      onChanged && onChanged();
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const disable = async () => {
    if (!window.confirm(`Turn off portal access for ${contact.email}?`)) return;
    setSaving(true);
    try {
      await API.delete(`/customer-contact/${contact.id}/portal`, { headers: authHeader() });
      notify("Portal disabled");
      setShow(false);
      onChanged && onChanged();
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const copy = (text, what) => {
    navigator.clipboard.writeText(text);
    notify(`${what} copied`);
  };

  const noEmail = !String(contact.email || "").trim();

  return (
    <>
      <button
        type="button"
        onClick={open}
        disabled={noEmail && !enabled}
        title={noEmail && !enabled ? "Add an email to this contact first" : ""}
        className={`btn btn-sm py-0 px-2 me-1 text-nowrap ${enabled ? "btn-success" : "btn-outline-primary"}`}
      >
        {enabled ? (
          <>
            <i className="mdi mdi-check"></i> Portal
          </>
        ) : (
          "Create Portal"
        )}
      </button>

      <Modal show={show} onHide={() => setShow(false)}>
        <Modal.Body className="shadow-lg bg-white px-3" style={{ borderRadius: 15 }}>
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0">{enabled || done ? "Customer Portal" : "Create Portal"}</h4>
            {enabled && <span className="badge bg-success">Active</span>}
          </div>
          <hr className="mt-2" />

          <div className="mb-2">
            <div className="text-muted small">Login (email)</div>
            <div className="fw-semibold">{contact.email}</div>
          </div>
          <div className="mb-3">
            <div className="text-muted small">Portal link</div>
            <div className="d-flex gap-1 align-items-center">
              <code className="text-break">{loginUrl}</code>
              <button type="button" className="btn btn-sm px-1" onClick={() => copy(loginUrl, "Link")} title="Copy link">
                <i className="mdi mdi-content-copy"></i>
              </button>
            </div>
          </div>

          {done ? (
            <div className="alert alert-success">
              <div className="mb-2">Saved. Send these to the customer:</div>
              <button
                type="button"
                className="btn btn-sm btn-success"
                onClick={() =>
                  copy(`Portal: ${loginUrl}\nEmail: ${done.email}\nPassword: ${done.password}`, "Login details")
                }
              >
                <i className="mdi mdi-content-copy"></i> Copy link + email + password
              </button>
            </div>
          ) : (
            <form onSubmit={save}>
              <label className="form-label">{enabled ? "New password" : "Password"}</label>
              <div className="input-group mb-1">
                <input
                  className="form-control"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={`At least ${MIN_PASSWORD} characters`}
                  autoComplete="new-password"
                />
                <button type="button" className="btn btn-outline-secondary" onClick={() => setPassword(makePassword())}>
                  Generate
                </button>
              </div>
              {error && <div className="text-danger small mb-2">{error}</div>}
              <div className="d-flex justify-content-between mt-3">
                <div>
                  {enabled && (
                    <button type="button" className="btn btn-outline-danger btn-sm" onClick={disable} disabled={saving}>
                      Disable portal
                    </button>
                  )}
                </div>
                <div>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShow(false)}>
                    Close
                  </button>
                  <button type="submit" className="btn btn-primary btn-sm ms-1" disabled={saving}>
                    {enabled ? "Reset password" : "Create Portal"}
                  </button>
                </div>
              </div>
            </form>
          )}
          {done && (
            <div className="d-flex justify-content-end">
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShow(false)}>
                Close
              </button>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}

export default ContactPortalButton;
